import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import ScrollAnimation from "../../common/ScrollAnimation";
import CustomPricingModal from "../../common/CustomPricingModal";
import { submitLead } from "../../../utils/franchiseApi";
import customCta from "../../../assets/image/customcta.svg";
import {
  LANDING_CONTAINER_PADDED,
  LANDING_INNER,
} from "../../../constants/landingPageLayout";

const GRADIENT_TEXT_STYLE = {
  backgroundImage: "linear-gradient(180deg, #7D3BEE 0%, #4F46E5 100%)",
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  color: "transparent",
  WebkitTextFillColor: "transparent",
};

const PricingCTA = ({
  showPricingDetailsLink = true,
  source = "Landing Page",
}) => {
  const [isCustomPricingOpen, setIsCustomPricingOpen] = useState(false);

  return (
    <section className="relative w-full bg-white pb-10 pt-2 sm:pb-12 sm:pt-3 lg:pb-16 lg:pt-4">
      <div className={LANDING_CONTAINER_PADDED}>
        <div className={LANDING_INNER}>
          <ScrollAnimation animationType="fadeUp">
            <div className="relative rounded-[20px] bg-[#0F172A] px-6 py-8 sm:px-10 sm:py-9 lg:px-14 lg:py-10">
              {/* Illustration — pops above the box on desktop */}
              <img
                src={customCta}
                alt=""
                aria-hidden
                className="pointer-events-none absolute bottom-3 right-5 hidden h-[calc(100%+110px)] w-auto max-w-[460px] object-contain lg:block xl:right-8"
                decoding="async"
              />

              <div className="relative z-10 grid gap-8 lg:grid-cols-2">
                <div className="flex flex-col items-center gap-5 text-center lg:items-start lg:text-left">
                  <h2
                    className="max-w-[391px] text-[clamp(30px,3.6vw,40px)] font-bold leading-[1.22] text-white"
                    style={{ fontFamily: '"Rethink Sans", sans-serif' }}
                  >
                    Flexible Pricing for{" "}
                    <span style={GRADIENT_TEXT_STYLE}>Every Business</span>
                  </h2>

                  <p
                    className="max-w-[413px] text-[18px] leading-[1.4] text-[#64748B]"
                    style={{ fontFamily: '"Inter", sans-serif', fontWeight: 400 }}
                  >
                    Get a personalized pricing plan tailored to your hiring goals,
                    team size, and recruitment needs.
                  </p>

                  <div className="mt-1 flex w-full flex-col items-center gap-3 sm:w-auto sm:flex-row sm:flex-wrap sm:justify-center lg:justify-start">
                    {showPricingDetailsLink && (
                      <Link
                        to="/pricing"
                        className="inline-flex h-[48px] w-full items-center justify-center gap-[7.46px] whitespace-nowrap rounded-[17.89px] border-[0.75px] border-[#E2E8F0] bg-white px-[10.44px] text-[14px] font-medium text-[#0F172A] transition-colors duration-200 hover:bg-[#F1F5F9] sm:w-auto"
                        style={{ fontFamily: '"Inter", sans-serif' }}
                      >
                        View Full Pricing Details
                      </Link>
                    )}

                    <span className="inline-flex items-center gap-0">
                      <button
                        type="button"
                        onClick={() => setIsCustomPricingOpen(true)}
                        className="inline-flex h-[48px] items-center justify-center whitespace-nowrap rounded-[17.89px] bg-[#4F46E5] px-[14px] text-[14px] font-medium leading-none tracking-normal text-white shadow-md transition-colors duration-200 hover:bg-[#4338CA]"
                        style={{ fontFamily: '"Inter", sans-serif' }}
                      >
                        Request a Custom Pricing
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsCustomPricingOpen(true)}
                        aria-label="Request a Custom Pricing"
                        className="inline-flex h-[48px] w-[48px] shrink-0 items-center justify-center rounded-full bg-[#4F46E5] p-[9px] text-white shadow-md transition-colors duration-200 hover:bg-[#4338CA]"
                      >
                        <ArrowUpRight className="h-4 w-4" strokeWidth={2.5} />
                      </button>
                    </span>
                  </div>

                  <p
                    className="mt-2 text-[16px] leading-none tracking-normal text-[#94A3B8]"
                    style={{
                      fontFamily: '"Inter", sans-serif',
                      fontWeight: 400,
                      lineHeight: "100%",
                      letterSpacing: "0",
                    }}
                  >
                    14-day free trial &bull; No credit card required &bull; Cancel
                    anytime &bull;{" "}
                    <Link
                      to="/contact"
                      className="font-medium text-[#818CF8] transition-colors hover:text-[#A5B4FC]"
                    >
                      Contact Sales
                    </Link>
                  </p>
                </div>

                {/* Illustration — in-flow on mobile/tablet */}
                <div className="flex justify-center lg:hidden">
                  <img
                    src={customCta}
                    alt="Flexible pricing illustration"
                    className="h-auto w-full max-w-[360px] object-contain"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </div>
            </div>
          </ScrollAnimation>
        </div>
      </div>

      <CustomPricingModal
        isOpen={isCustomPricingOpen}
        onClose={() => setIsCustomPricingOpen(false)}
        source={source}
        onSubmit={submitLead}
      />
    </section>
  );
};

export default PricingCTA;
